import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Select,
  Space,
  Steps,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useMutation, useQuery } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { epcService } from '@/services/epc.service';
import { projectService } from '@/services/project.service';
import {
  downloadEst3WbsImportCsvTemplate,
  parseEst3WbsImportFile,
  type Est3WbsImportPreviewRow,
  type Est3WbsImportPreset,
  WBS_IMPORT_PRESET_HINTS,
  WBS_IMPORT_PRESET_LABELS,
} from '@/utils/est3WbsImportParser';
import { buildWbsTableTree } from '@/utils/est3WbsTree';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

export interface EpcEstimateWbsImportWizardProps {
  open: boolean;
  onClose: () => void;
  estimateId: string;
  revisionId: string;
  readOnly?: boolean;
  onImported: () => void;
  defaultProjectId?: string;
}

export const EpcEstimateWbsImportWizard: React.FC<EpcEstimateWbsImportWizardProps> = ({
  open,
  onClose,
  estimateId,
  revisionId,
  readOnly,
  onImported,
  defaultProjectId,
}) => {
  const [tab, setTab] = useState<'excel' | 'project'>('excel');
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [uploadFile, setUploadFile] = useState<UploadFile | null>(null);
  const [preview, setPreview] = useState<Est3WbsImportPreviewRow[]>([]);
  const [result, setResult] = useState<any>(null);
  const [projectId, setProjectId] = useState<string | undefined>(defaultProjectId);
  const [structureId, setStructureId] = useState<string | undefined>();
  const [importPreset, setImportPreset] = useState<Est3WbsImportPreset>('auto');
  const [detectedPreset, setDetectedPreset] = useState<string | undefined>();

  const parseFile = async (f: File, preset: Est3WbsImportPreset) => {
    const rows = await parseEst3WbsImportFile(f, preset);
    setPreview(rows);
    setDetectedPreset(rows[0]?.detectedPreset);
    setStep(1);
  };

  const reset = () => {
    setTab('excel');
    setStep(0);
    setFile(null);
    setUploadFile(null);
    setPreview([]);
    setResult(null);
    setProjectId(defaultProjectId);
    setStructureId(undefined);
    setImportPreset('auto');
    setDetectedPreset(undefined);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const projectsQ = useQuery({
    queryKey: ['projects-selection'],
    queryFn: () => projectService.getProjectsForSelection(),
    enabled: open && tab === 'project',
  });

  const structuresQ = useQuery({
    queryKey: ['epc-wbs-structures', projectId],
    queryFn: () => epcService.getWbsStructures(projectId!),
    enabled: open && tab === 'project' && !!projectId,
  });

  const projectNodesQ = useQuery({
    queryKey: ['epc-wbs-nodes-preview', structureId],
    queryFn: () => epcService.getWbsNodes(structureId!),
    enabled: open && tab === 'project' && !!structureId,
  });

  const projectTree = useMemo(() => {
    const nodes = (projectNodesQ.data || []) as Array<{
      id: string;
      code: string;
      title?: string;
      name?: string;
      parent_id?: string | null;
    }>;
    return buildWbsTableTree(
      nodes.map((n) => ({
        id: n.id,
        code: n.code,
        name: n.title || n.name || n.code,
        parent_id: n.parent_id,
      }))
    );
  }, [projectNodesQ.data]);

  const importM = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file');
      return epcEstimationService.importWbs(estimateId, revisionId, file, importPreset);
    },
    onSuccess: (data) => {
      setResult(data);
      setStep(2);
      message.success(`Imported ${data?.created ?? 0} WBS node(s)`);
      onImported();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Import failed'),
  });

  const copyM = useMutation({
    mutationFn: () =>
      epcEstimationService.copyWbsFromProject(estimateId, {
        revisionId,
        projectId: projectId!,
        structureId,
      }),
    onSuccess: (data) => {
      setResult(data);
      setStep(2);
      message.success(`Copied ${data?.created ?? 0} WBS node(s) from project`);
      onImported();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Copy failed'),
  });

  const validCount = useMemo(() => preview.filter((r) => r.status === 'valid').length, [preview]);
  const errorCount = useMemo(() => preview.filter((r) => r.status === 'error').length, [preview]);

  const structures = (structuresQ.data || []) as Array<{ id: string; name?: string; is_primary?: boolean }>;
  const primaryStructure = structures.find((s) => s.is_primary) || structures[0];

  return (
    <Modal
      title="Set up WBS"
      open={open}
      onCancel={handleClose}
      width={860}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={handleClose}>{step === 2 ? 'Close' : 'Cancel'}</Button>
          {tab === 'excel' && step === 1 && (
            <Button
              type="primary"
              disabled={readOnly || validCount === 0}
              loading={importM.isPending}
              onClick={() => importM.mutate()}
            >
              Import {validCount} valid row(s)
            </Button>
          )}
          {tab === 'project' && step === 1 && (
            <Button
              type="primary"
              disabled={readOnly || !projectId || !projectTree.length}
              loading={copyM.isPending}
              onClick={() => copyM.mutate()}
            >
              Copy {projectTree.length} node(s)
            </Button>
          )}
        </Space>
      }
    >
      {readOnly ? (
        <Alert type="warning" showIcon message="Approved revision — WBS setup is read-only." />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Tabs
            activeKey={tab}
            onChange={(k) => {
              setTab(k as 'excel' | 'project');
              setStep(0);
              setResult(null);
            }}
            items={[
              { key: 'excel', label: 'Import Excel' },
              { key: 'project', label: 'Copy from project' },
            ]}
          />

          {tab === 'excel' ? (
            <>
              <Steps
                size="small"
                current={step}
                items={[{ title: 'Upload' }, { title: 'Preview' }, { title: 'Done' }]}
              />
              {step === 0 && (
                <>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Text type="secondary">Source format</Text>
                    <Select
                      style={{ width: '100%' }}
                      value={importPreset}
                      onChange={setImportPreset}
                      options={(
                        ['auto', 'est3', 'primavera', 'msproject'] as Est3WbsImportPreset[]
                      ).map((k) => ({
                        value: k,
                        label: WBS_IMPORT_PRESET_LABELS[k],
                      }))}
                    />
                    <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                      {WBS_IMPORT_PRESET_HINTS[importPreset]}
                    </Paragraph>
                  </Space>
                  <Space>
                    <Button icon={<DownloadOutlined />} onClick={() => downloadEst3WbsImportCsvTemplate()}>
                      Download CSV template
                    </Button>
                    <Button
                      type="link"
                      onClick={async () => {
                        try {
                          const blob = await epcEstimationService.downloadWbsImportTemplate(estimateId);
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'est3-wbs-import-template.xlsx';
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch (e: any) {
                          message.error(e?.message || 'Template download failed');
                        }
                      }}
                    >
                      Download Excel template (server)
                    </Button>
                  </Space>
                  <Dragger
                    multiple={false}
                    maxCount={1}
                    accept=".xlsx,.xls,.csv"
                    fileList={uploadFile ? [uploadFile] : []}
                    beforeUpload={async (f) => {
                      setFile(f);
                      setUploadFile({ uid: f.name, name: f.name, status: 'done' });
                      try {
                        await parseFile(f, importPreset);
                      } catch (e: any) {
                        message.error(e?.message || 'Failed to parse file');
                      }
                      return false;
                    }}
                    onRemove={() => {
                      setFile(null);
                      setUploadFile(null);
                      setPreview([]);
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Drop WBS Excel/CSV here</p>
                  </Dragger>
                </>
              )}
              {step === 1 && (
                <>
                  <Space wrap>
                    <Tag color="blue">
                      Format:{' '}
                      {WBS_IMPORT_PRESET_LABELS[
                        (detectedPreset as Est3WbsImportPreset) || 'est3'
                      ] || detectedPreset}
                    </Tag>
                    <Select
                      size="small"
                      value={importPreset}
                      style={{ minWidth: 220 }}
                      onChange={async (v: Est3WbsImportPreset) => {
                        setImportPreset(v);
                        if (file) {
                          try {
                            await parseFile(file, v);
                          } catch (e: any) {
                            message.error(e?.message || 'Re-parse failed');
                          }
                        }
                      }}
                      options={(
                        ['auto', 'est3', 'primavera', 'msproject'] as Est3WbsImportPreset[]
                      ).map((k) => ({
                        value: k,
                        label: WBS_IMPORT_PRESET_LABELS[k],
                      }))}
                    />
                    <Tag color="green">{validCount} valid</Tag>
                    <Tag color={errorCount ? 'red' : 'default'}>{errorCount} errors</Tag>
                    <Button size="small" onClick={() => setStep(0)}>
                      Back
                    </Button>
                  </Space>
                  <Table
                    size="small"
                    rowKey="key"
                    dataSource={preview}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: true }}
                    columns={[
                      { title: 'Row', dataIndex: 'row', width: 60 },
                      { title: 'Code', dataIndex: 'code', width: 100 },
                      { title: 'Name', dataIndex: 'name', ellipsis: true },
                      { title: 'Parent', dataIndex: 'parentCode', width: 100 },
                      { title: 'Sort', dataIndex: 'sortOrder', width: 70 },
                      {
                        title: 'Status',
                        width: 160,
                        render: (_: unknown, r: Est3WbsImportPreviewRow) =>
                          r.status === 'valid' ? (
                            <Tag color="green">valid</Tag>
                          ) : (
                            <Tag color="red">{r.errorMessage}</Tag>
                          ),
                      },
                    ]}
                  />
                </>
              )}
            </>
          ) : (
            <>
              <Steps
                size="small"
                current={step}
                items={[{ title: 'Select project' }, { title: 'Preview' }, { title: 'Done' }]}
              />
              {step === 0 && (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text type="secondary">
                    Copy the project WBS structure into this estimate revision. Existing codes are
                    skipped.
                  </Text>
                  <Select
                    showSearch
                    placeholder="Select project"
                    style={{ width: '100%' }}
                    value={projectId}
                    onChange={(v) => {
                      setProjectId(v);
                      setStructureId(undefined);
                    }}
                    options={(projectsQ.data || []).map((p: any) => ({
                      value: p.id,
                      label: p.project_number ? `${p.project_number} · ${p.name}` : p.name,
                    }))}
                    filterOption={(input, opt) =>
                      String(opt?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                  {projectId && structures.length > 1 ? (
                    <Select
                      placeholder="WBS structure"
                      style={{ width: '100%' }}
                      value={structureId || primaryStructure?.id}
                      onChange={setStructureId}
                      options={structures.map((s) => ({
                        value: s.id,
                        label: s.name || s.id.slice(0, 8),
                      }))}
                    />
                  ) : null}
                  <Button
                    type="primary"
                    disabled={!projectId}
                    onClick={() => {
                      if (!structureId && primaryStructure) setStructureId(primaryStructure.id);
                      setStep(1);
                    }}
                  >
                    Preview WBS tree
                  </Button>
                </Space>
              )}
              {step === 1 && (
                <>
                  <Space>
                    <Tag>{projectTree.length} root node(s)</Tag>
                    <Button size="small" onClick={() => setStep(0)}>
                      Back
                    </Button>
                  </Space>
                  <Table
                    size="small"
                    rowKey="id"
                    loading={projectNodesQ.isLoading}
                    dataSource={projectTree}
                    pagination={false}
                    expandable={{ defaultExpandAllRows: true }}
                    columns={[
                      { title: 'Code', dataIndex: 'code', width: 120 },
                      { title: 'Name', dataIndex: 'name', ellipsis: true },
                    ]}
                  />
                </>
              )}
            </>
          )}

          {step === 2 && (
            <Alert
              type="success"
              showIcon
              message={
                tab === 'excel'
                  ? `Created ${result?.created ?? 0} node(s); linked ${result?.linkedParents ?? 0} parent(s)`
                  : `Created ${result?.created ?? 0}; skipped ${result?.skipped ?? 0} duplicate(s)`
              }
              description={
                result?.errors?.length
                  ? `${result.errors.length} warning(s) — see server response.`
                  : undefined
              }
            />
          )}
        </Space>
      )}
    </Modal>
  );
};
